import { signInWithCustomToken, signOut as firebaseSignOut, onAuthStateChanged } from 'firebase/auth';
import { getAuthAsync } from 'configuration';
import { API_ENDPOINTS, REQUEST_OPTIONS, HTTP_METHODS } from 'constants';

// Helper to detect Safari (most restrictive with cookies)
// Exported so other services (e.g. reviews) can use it for token refresh guards
export const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

// Helper to detect mobile browsers
const isMobileBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    userAgent.toLowerCase(),
  );
};

// Treat Safari and mobile as needing token auth
const needsTokenAuth = isMobileBrowser() || isSafari();
const useOpaqueCode = process.env.REACT_APP_OPAQUE_CODE_AUTH === 'true';
let mobileSessionToken = null;

// AUD-12: cache the Firebase custom token so we don't mint a new one
// on every page load. Token expires after 1 hour so cache for 55 minutes.
let cachedCustomToken = null;
let cachedCustomTokenExpiry = 0;
const CUSTOM_TOKEN_TTL_MS = 55 * 60 * 1000;

// Clean a parameter from the URL bar without triggering a navigation.
const removeUrlParam = (param) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(param);
    const newUrl = window.location.pathname +
      (urlParams.toString() ? '?' + urlParams.toString() : '') +
      window.location.hash;
    window.history.replaceState({}, '', newUrl);
  } catch (e) {
    console.error('Failed to clean URL param:', e);
  }
};

// SEC-01: on flag-on path, read ?code= from URL and exchange it for a
// session cookie via POST /auth/exchange-code. The cookie is set in
// the response to that request, which originates from this app's own
// domain — not cross-origin — so Safari ITP does not drop it.
//
// On flag-off legacy path, read ?session_token= and store in localStorage.
const initializeFromUrl = async () => {
  if (!needsTokenAuth) return;

  try {
    const urlParams = new URLSearchParams(window.location.search);

    if (useOpaqueCode) {
      const code = urlParams.get('code');
      if (code) {
        removeUrlParam('code');
        // Exchange the code — server sets the session cookie in response
        const res = await fetch(API_ENDPOINTS.AUTH.EXCHANGE_CODE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });
        if (!res.ok) {
          console.warn('[auth] Code exchange failed, redirecting to sign-in');
          // Code expired or already used — send back to sign-in
          const signInUrl = new URL(API_ENDPOINTS.AUTH.SIGN_IN);
          signInUrl.searchParams.set('returnUrl', window.location.href);
          window.location.href = signInUrl.toString();
        }
        // Give Safari a tick to commit the Set-Cookie response into the
        // browser jar before checkAuthenticationStatus fires its status check.
        await new Promise((r) => setTimeout(r, 200));
      }
    } else {
      // Legacy path
      const tokenFromUrl = urlParams.get('session_token');
      if (tokenFromUrl) {
        localStorage.setItem('mobile_session_token', tokenFromUrl);
        mobileSessionToken = tokenFromUrl;
        removeUrlParam('session_token');
      }
    }
  } catch (e) {
    console.error('Failed to initialize from URL:', e);
  }
};

// Run eagerly at module load — async so code exchange can happen before
// checkAuthenticationStatus fires. Callers await initUrlPromise if needed.
const initUrlPromise = initializeFromUrl();

// Also load stored token from localStorage for legacy path
if (!useOpaqueCode && needsTokenAuth && !mobileSessionToken) {
  try {
    mobileSessionToken = localStorage.getItem('mobile_session_token');
  } catch (e) {
    console.error('Failed to load mobile session token:', e);
  }
}

const signInWithFirebase = async (customToken) => {
  try {
    const auth = await getAuthAsync();
    const userCredential = await signInWithCustomToken(auth, customToken);
    const user = userCredential.user;

    return { success: true, user };
  } catch (error) {
    console.error('Firebase sign-in failed:', error);
    return { success: false, error };
  }
};

const checkAuthenticationStatus = async () => {
  // Ensure URL initialisation (code exchange or token read) has completed
  // before we check auth status, otherwise the cookie won't be set yet.
  await initUrlPromise;

  try {
    // Build headers - include auth token for Safari/mobile (legacy path)
    const headers = {
      ...REQUEST_OPTIONS.DEFAULT.headers,
    };

    if (!useOpaqueCode && needsTokenAuth && mobileSessionToken) {
      headers["Authorization"] = `Bearer ${mobileSessionToken}`;
    }

    // First, check authentication status
    const statusResponse = await fetch(API_ENDPOINTS.AUTH.STATUS, {
      ...REQUEST_OPTIONS.DEFAULT,
      method: HTTP_METHODS.GET,
      headers,
    });

    if (!statusResponse.ok) {
      throw new Error(`HTTP error! status: ${statusResponse.status}`);
    }

    const statusData = await statusResponse.json();

    // If authenticated on server, get custom token for Firebase sign-in.
    // AUD-12: skip the /auth/token fetch if we have a cached token that
    // hasn't expired yet — saves one round-trip on every page load.
    if (statusData.authenticated) {
      const now = Date.now();
      if (cachedCustomToken && now < cachedCustomTokenExpiry) {
        await signInWithFirebase(cachedCustomToken);
      } else {
        const tokenResponse = await fetch(API_ENDPOINTS.AUTH.TOKEN, {
          ...REQUEST_OPTIONS.DEFAULT,
          method: HTTP_METHODS.POST,
          headers,
        });

        if (tokenResponse.ok) {
          const tokenData = await tokenResponse.json();
          if (tokenData.customToken) {
            cachedCustomToken = tokenData.customToken;
            cachedCustomTokenExpiry = now + CUSTOM_TOKEN_TTL_MS;
            await signInWithFirebase(tokenData.customToken);
          }
        }
      }
    } else {
      // Clear cached token on sign-out
      cachedCustomToken = null;
      cachedCustomTokenExpiry = 0;
    }

    return {
      success: true,
      data: statusData,
    };
  } catch (error) {
    console.error('Auth check failed:', error);
    return { success: false, error: error.message };
  }
};

const signOut = async () => {
  try {
    const auth = await getAuthAsync();
    if (auth.currentUser) {
      await firebaseSignOut(auth);
    }

    // Build headers with token for Safari/mobile
    const headers = {
      ...REQUEST_OPTIONS.DEFAULT.headers,
    };

    if (!useOpaqueCode && needsTokenAuth && mobileSessionToken) {
      headers["Authorization"] = `Bearer ${mobileSessionToken}`;
    }

    const response = await fetch(API_ENDPOINTS.AUTH.SIGN_OUT, {
      ...REQUEST_OPTIONS.DEFAULT,
      method: HTTP_METHODS.POST,
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Clear mobile session token and cached custom token
    if (needsTokenAuth) {
      mobileSessionToken = null;
      localStorage.removeItem("mobile_session_token");
      localStorage.removeItem("mobile_session_uid");
    }
    cachedCustomToken = null;
    cachedCustomTokenExpiry = 0;

    return { success: true };
  } catch (error) {
    console.error('Sign out failed:', error);
    return { success: false, error: error.message };
  }
};

const redirectToSignIn = (returnUrl = null) => {
  const url = new URL(API_ENDPOINTS.AUTH.SIGN_IN);

  if (returnUrl) {
    url.searchParams.set('returnUrl', returnUrl);
    url.searchParams.set('source', 'client');
  }

  window.location.href = url.toString();
};

const syncServerSession = async (user) => {
  if (!user) return { success: false, error: 'No user provided' };

  try {
    if (needsTokenAuth) {
      mobileSessionToken = localStorage.getItem("mobile_session_token");
      if (!mobileSessionToken) {
        await new Promise(resolve => setTimeout(resolve, 100));
        mobileSessionToken = localStorage.getItem("mobile_session_token");
      }
    }

    const result = await checkAuthenticationStatus();
    return result;
  } catch (error) {
    console.error('Session sync failed:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Force-refresh the Firebase ID token if needed.
 * On Safari, tokens can silently expire due to ITP blocking cookie-based refresh.
 * Call this before any Firestore write that requires auth (e.g. createReview).
 * Returns the current Firebase user, or null if unauthenticated.
 */
export const getValidFirebaseToken = async () => {
  try {
    const auth = await getAuthAsync();
    const currentUser = auth.currentUser;
    if (!currentUser) return null;
    // Force refresh on Safari; use cached token on other browsers
    await currentUser.getIdToken(isSafari());
    return currentUser;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
};

/**
 * Subscribe to Firebase auth state changes.
 * Used by AuthenticationContext to detect when Firebase silently loses
 * its session (e.g. Safari ITP expiry) while React state still shows logged in.
 * Returns the unsubscribe function.
 */
export const subscribeToFirebaseAuthState = async (onChange) => {
  const auth = await getAuthAsync();
  return onAuthStateChanged(auth, onChange);
};

export const authenticationService = {
  checkAuthenticationStatus,
  signOut,
  redirectToSignIn,
  syncServerSession,
  getValidFirebaseToken,
  subscribeToFirebaseAuthState,
};